import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes as Switch,
} from "react-router-dom";

import React, { PropsWithChildren, ReactElement, Suspense, lazy } from "react";

import IsLoader from "components/Common/IsLoader";
import {
  LanguageRedirectAr,
  LanguageRedirectEn,
} from "components/LanguageRedirect";
import { ParticipantProvider } from "components/Meeting/Live/ParticipantProvider";
import VideoProvider from "components/Meeting/Live/VideoProvider";

import useLanguageRedirect from "hooks/custom/useCustomRoute";

import { selectAuth } from "store/auth/selectors";
import { User } from "store/auth/types";
import { useAppSelector } from "store/hooks";

import routes from "./routes";

// landing pages start
const LandingHome = lazy(() => import("pages/Landing/Home"));
const LandingPlans = lazy(() => import("pages/Landing/Plans"));
const LandingWhyUs = lazy(() => import("pages/Landing/WhyUs"));
const LoadingSiteBlogs = lazy(() => import("pages/Landing/siteBlogs"));
const LoadingSiteBlogDetails = lazy(
  () => import("pages/Landing/siteBlogs/BlogDetails")
);
const LandingContactUs = lazy(() => import("pages/Landing/ContactUs"));
// landing pages end
const Login = lazy(() => import("pages/Auth/Login"));
const SignUp = lazy(() => import("pages/Auth/SignUp"));
const ThankYou = lazy(() => import("pages/Auth/ThankYou"));
const Home = lazy(() => import("pages/Main"));
const Calendar = lazy(() => import("pages/Calendar"));
const Meeting = lazy(() => import("pages/Meetings/Meeting"));
const MeetingSummary = lazy(() => import("pages/Meetings/Summary"));
const ArchivedMeetings = lazy(() => import("pages/Meetings/Archive"));
const AddMeeting = lazy(() => import("pages/Meetings/Form"));
const Documents = lazy(() => import("pages/Documents"));
const Messages = lazy(() => import("pages/Messages"));
const Votings = lazy(() => import("pages/Votings/List"));
const Voting = lazy(() => import("pages/Votings/Voting"));
const Workspaces = lazy(() => import("pages/Workspaces/List"));
const Workspace = lazy(() => import("pages/Workspaces/Workspace"));
const Reset = lazy(() => import("pages/Auth/ResetPassword"));
const Settings = lazy(() => import("pages/Settings"));
const RolesAndPermissions = lazy(() => import("pages/RolesAndPermissions"));
const Users = lazy(() => import("pages/Users/List"));
const AdminUsers = lazy(() => import("pages/adminUsers/list"));
const EditUser = lazy(() => import("pages/Users/Form"));
const CompanyForm = lazy(() => import("pages/Company/Form"));
const WorkspaceForm = lazy(() => import("pages/Workspaces/Form"));
const Tasks = lazy(() => import("pages/Tasks/List"));
const TaskForm = lazy(() => import("pages/Tasks/Form"));
const LiveMeeting = lazy(() => import("pages/Meetings/LiveMeeting"));
const OfflineMeeting = lazy(() => import("pages/Meetings/OfflineMeeting"));
const LiveVoting = lazy(() => import("pages/Votings/LiveVoting"));
const OfflineVoting = lazy(() => import("pages/Votings/OfflineVoting"));
const Task = lazy(() => import("pages/Tasks/Task"));
const GlobalTask = lazy(() => import("pages/Tasks/Task"));
const VotingForm = lazy(() => import("pages/Votings/Form"));
const VotingSummary = lazy(() => import("pages/Votings/Summary"));
const ErrorPage = lazy(() => import("pages/404"));
const ForbiddenPage = lazy(() => import("pages/403"));

const MeetingPage = lazy(() => import("pages/UI/Meeting"));
const DetailPage = lazy(() => import("pages/UI/Details"));
const LiveMeetingPage = lazy(() => import("pages/UI/LiveMeeting"));
const LiveMeetingPublicPage = lazy(() => import("pages/UI/LiveMeetingPublic"));
const MeetingDetailsPage = lazy(() => import("pages/UI/MeetingDetails"));
const MeetingDetailSummaryPage = lazy(
  () => import("pages/UI/MeetingDetailSummary")
);
const MeetingMinutesPage = lazy(() => import("pages/UI/MeetingMinutes"));
const MeetingSummaryUI = lazy(() => import("pages/UI/MeetingSummary"));
const VideoRecord = lazy(() => import("pages/UI/VideoRecord"));
const SettingsUI = lazy(() => import("pages/UI/Settings"));
const WorkSpaceDetails = lazy(() => import("pages/UI/WorkSpaceDetails"));

// content pages start
const TermsConditions = lazy(() => import("pages/Landing/TermsConditions"));
const PrivacyPolicy = lazy(() => import("pages/Landing/PrivacyPolicy"));
// content pages end

const ProtectedRoute = ({
  user,
  redirectPath = "/login",
}: {
  user: User | null;
  redirectPath?: string;
}) => {
  if (!user) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

export const LanguageRedirector = () => {
  useLanguageRedirect();
  return null;
};

const Routes: React.FC<PropsWithChildren> = ({ children }): ReactElement => {
  const {
    home,
    auth,
    calendar,
    meetings,
    documents,
    messages,
    votings,
    tasks,
    workspaces,
    settings,
    users,
    company,
    ui,
    errors,
    landing,
    contentPages,
    admin,
  } = routes;

  const authState = useAppSelector(selectAuth);
  return (
    <BrowserRouter>
      <LanguageRedirector />
      <VideoProvider>
        <ParticipantProvider>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-screen">
                <IsLoader width={140} height={140} />
              </div>
            }
          >
            <Switch>
              <Route
                path={landing.languageAr}
                element={<LanguageRedirectAr />}
              />
              <Route
                path={landing.languageEn}
                element={<LanguageRedirectEn />}
              />
              <Route path={auth.login} element={<Login />} />
              <Route path={auth.signUp} element={<SignUp />} />
              <Route path={auth.thankYou} element={<ThankYou />} />
              <Route path={auth.resetPassword} element={<Reset />} />
              <Route path={auth.firstLogin} element={<Reset />} />
              <Route path={errors.forbidden} element={<ForbiddenPage />} />
              <Route path={landing.home} element={<LandingHome />} />
              <Route path={landing.plans} element={<LandingPlans />} />
              <Route path={landing.whyUs} element={<LandingWhyUs />} />
              <Route path={landing.blogs} element={<LoadingSiteBlogs />} />
              <Route
                path={landing.blogDetails}
                element={<LoadingSiteBlogDetails />}
              />
              <Route path={landing.plansAr} element={<LandingPlans />} />
              <Route path={landing.whyUsAr} element={<LandingWhyUs />} />
              <Route path={landing.contactUs} element={<LandingContactUs />} />
              <Route
                path={landing.contactUsAr}
                element={<LandingContactUs />}
              />
              <Route path={contentPages.terms} element={<TermsConditions />} />
              <Route path={contentPages.privacy} element={<PrivacyPolicy />} />
              <Route
                path={ui.meetings.liveItemPublic}
                element={<LiveMeetingPublicPage />}
              />
              <Route element={<ProtectedRoute user={authState.user} />}>
                <Route path={home} element={<Home />} />
                <Route path={calendar} element={<Calendar />} />
                <Route path={meetings.item} element={<Meeting />} />
                <Route
                  path={meetings.summaryItem}
                  element={<MeetingSummary />}
                />
                <Route path={meetings.add} element={<AddMeeting />} />
                <Route path={meetings.archive} element={<ArchivedMeetings />} />
                <Route path={meetings.edit} element={<AddMeeting />} />
                <Route path={meetings.liveItem} element={<LiveMeeting />} />
                <Route path={meetings.offline} element={<OfflineMeeting />} />
                <Route path={votings.liveItem} element={<LiveVoting />} />
                <Route path={votings.offlineItem} element={<OfflineVoting />} />
                <Route path={documents} element={<Documents />} />
                <Route path={messages} element={<Messages />} />
                <Route path={votings.list} element={<Votings />} />
                <Route path={votings.item} element={<Voting />} />
                <Route path={votings.add} element={<VotingForm />} />
                <Route path={votings.edit} element={<VotingForm />} />
                <Route path={votings.summaryItem} element={<VotingSummary />} />
                <Route path={tasks.list} element={<Tasks />} />
                <Route path={tasks.add} element={<TaskForm />} />
                <Route path={tasks.item} element={<Task />} />
                <Route path={tasks.globalItem} element={<GlobalTask />} />
                <Route path={tasks.edit} element={<TaskForm />} />
                <Route path={workspaces.list} element={<Workspaces />} />
                <Route path={workspaces.item} element={<Workspace />} />
                <Route
                  path={workspaces.createWorkspace}
                  element={<WorkspaceForm />}
                />
                <Route path={workspaces.edit} element={<WorkspaceForm />} />

                <Route path={settings.page} element={<Settings />} />
                <Route path={company.form} element={<CompanyForm />} />
                <Route
                  path={settings.rolesAndPermission}
                  element={<RolesAndPermissions />}
                />
                <Route path={users.list} element={<Users />} />
                <Route path={users.edit} element={<EditUser />} />
                <Route path={users.add} element={<EditUser />} />
                {/* New Routes */}
                <Route path={ui.meetings.list} element={<MeetingPage />} />
                <Route path={ui.meetings.detail} element={<DetailPage />} />
                <Route
                  path={ui.meetings.item}
                  element={<MeetingDetailsPage />}
                />
                <Route
                  path={ui.meetings.liveItem}
                  element={<LiveMeetingPage />}
                />
                <Route
                  path={ui.meetings.itemSummary}
                  element={<MeetingDetailSummaryPage />}
                />
                <Route
                  path={ui.meetings.meetingMinutes}
                  element={<MeetingMinutesPage />}
                />
                <Route
                  path={ui.meetings.summary}
                  element={<MeetingSummaryUI />}
                />
                <Route path={ui.videoRecord.show} element={<VideoRecord />} />
                <Route path={ui.settings.show} element={<SettingsUI />} />
                <Route
                  path={ui.workspace.details}
                  element={<WorkSpaceDetails />}
                />
              </Route>
              <Route path={admin.users.list} element={<AdminUsers />} />
              <Route path="*" element={<ErrorPage />} />
            </Switch>
          </Suspense>
          {children}
        </ParticipantProvider>
      </VideoProvider>
    </BrowserRouter>
  );
};

export default Routes;
